import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Info, RotateCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from 'components/ui/breadcrumb';
import { Button } from 'components/ui/button';
import {
  EmbeddedStatus,
  IKnowledgeBaseItem,
} from 'features/chat/agent-chat-module/types/kb.service.type';
import { useConfig } from 'hooks/use-config';

import { getKbName, isExternalWriteup, getWriteupSourceUrl } from '../utils';
import { KbDocumentContent } from './kb-document-content';
import { KbDocumentInfo } from './kb-document-info';
import { KbEmbedStatus } from './kb-embed-status';
import { KbReembedDialog } from './kb-reembed-dialog';

type KbDocumentPageProps = {
  kb: IKnowledgeBaseItem;
  folderName?: string;
  canEdit?: boolean;
  onBack?: () => void;
  onReembedStarted?: () => void;
};

export function KbDocumentPage({
  kb,
  folderName,
  canEdit = true,
  onBack,
  onReembedStarted,
}: KbDocumentPageProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { configurationSettings } = useConfig();
  const projectKey = configurationSettings?.blocksKey || '';
  const [isReEmbedOpen, setIsReEmbedOpen] = useState(false);
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState<EmbeddedStatus | null>(null);

  canEdit = canEdit && !isExternalWriteup(kb);
  const sourceUrl = getWriteupSourceUrl(kb);
  const documentTitle = getKbName(kb);
  const embedStatus = overrideStatus || kb.embad_status || EmbeddedStatus.COMPLETED;

  useEffect(() => {
    setOverrideStatus(null);
  }, [kb.embad_status]);

  const handleFolderBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    navigate(-1);
  };

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-4 overflow-hidden">
      <Breadcrumb className="hidden shrink-0 md:flex">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/knowledge-base" className="text-foreground hover:text-foreground">
                {t('KNOWLEDGE_BASE') || 'Knowledge Base'}
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <button
                type="button"
                className="cursor-pointer text-foreground hover:text-foreground"
                onClick={handleFolderBack}
              >
                {folderName || t('FOLDER') || 'Folder'}
              </button>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="text-muted-foreground">{documentTitle}</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {isExternalWriteup(kb) && (
        <div className="shrink-0 rounded-md border bg-muted/40 p-3 text-sm">
          <strong>{kb.meta_data.ExternalSource?.label || kb.meta_data.ExternalSource?.system}</strong> · Synced writeups are read-only here. Manage this content in its source application.
          {sourceUrl && <a className="ml-2 underline" href={sourceUrl} target="_blank" rel="noopener noreferrer">Open source</a>}
        </div>
      )}
      <DocumentHeader
        documentTitle={documentTitle}
        embedStatus={embedStatus}
        canEdit={canEdit}
        onReembed={() => setIsReEmbedOpen(true)}
        onToggleInfo={() => setIsInfoOpen(!isInfoOpen)}
      />

      <div className="flex min-h-0 flex-1 gap-4 overflow-hidden">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg bg-muted/40 p-1">
          <KbDocumentContent kb={kb} canEdit={canEdit} />
        </div>
        {isInfoOpen && (
          <KbDocumentInfo kb={kb} open={isInfoOpen} onOpenChange={setIsInfoOpen} />
        )}
      </div>

      {canEdit ? (
        <KbReembedDialog
          open={isReEmbedOpen}
          onOpenChange={setIsReEmbedOpen}
          projectKey={projectKey}
          kbs={[{ id: kb.id, type: kb.kb_type }]}
          documentName={documentTitle}
          onSuccess={() => {
            setOverrideStatus(EmbeddedStatus.PENDING);
            onReembedStarted?.();
          }}
        />
      ) : null}
    </div>
  );
}

type DocumentHeaderProps = {
  documentTitle: string;
  embedStatus: EmbeddedStatus;
  canEdit: boolean;
  onReembed: () => void;
  onToggleInfo: () => void;
};

function DocumentHeader({
  documentTitle,
  embedStatus,
  canEdit,
  onReembed,
  onToggleInfo,
}: DocumentHeaderProps) {
  const { t } = useTranslation();

  return (
    <div className="flex w-full shrink-0 flex-col justify-between gap-3 md:flex-row md:items-center md:gap-0">
      <div className="flex w-full flex-col gap-2 overflow-hidden md:w-auto md:flex-row md:items-center md:gap-3">
        <h1 className="w-full truncate text-xl font-bold tracking-tight md:w-auto" title={documentTitle}>
          {documentTitle}
        </h1>
        <KbEmbedStatus status={embedStatus} />
      </div>
      <div className="flex w-full items-center gap-2 md:w-auto">
        {canEdit ? (
          <Button variant="outline" size="sm" className="gap-2" onClick={onReembed}>
            <RotateCw className="h-4 w-4" />
            {t('RE_EMBED') || 'Re-embed'}
          </Button>
        ) : null}
        <Button variant="outline" size="sm" className="gap-2" onClick={onToggleInfo}>
          <Info className="h-4 w-4" />
          {t('INFO') || 'Info'}
        </Button>
      </div>
    </div>
  );
}
